import React, { useState } from 'react';
import AssignmentList from './AssignmentList';
import { Alert, Button, List, message, Modal, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import api from '../../authentication/axiosInstance';

const { Text, Paragraph } = Typography;

const RESOURCE_PATHS = {
  vehicle: '/vehicles',
  staff: '/staff',
  equipment: '/equipment',
  site: '/sites',
  plow: '/plows',
  doubledown: '/double-down',
};

const TYPE_LABELS = {
  vehicle: 'vehicle',
  staff: 'staff member',
  equipment: 'equipment',
  site: 'site',
  plow: 'plow',
  doubledown: 'double-down',
};

const ARCHIVE_CONSEQUENCE_COPY = {
  vehicle: 'Saved and unsaved dispatch boards assigned to this vehicle will be removed. Dispatch history is kept.',
  staff: 'This staff member will be removed from active and draft allocation setups and live assignments. Unsaved dispatch boards containing them will be removed. Saved dispatch boards are kept with only this staff member removed. Dispatch history is kept.',
  member: 'Unsaved dispatch boards containing this item will be removed. Saved dispatch boards are kept, but this item will be removed from them. Dispatch history is kept.',
  attachment: 'Unsaved dispatch boards containing this attachment will be removed. Saved dispatch boards are kept, but the attachment will be removed from them. Dispatch history is kept.',
};

const getArchiveConsequenceCopy = (itemType) => {
  if (itemType === 'vehicle') return ARCHIVE_CONSEQUENCE_COPY.vehicle;
  if (itemType === 'staff') return ARCHIVE_CONSEQUENCE_COPY.staff;
  if (itemType === 'plow' || itemType === 'doubledown') return ARCHIVE_CONSEQUENCE_COPY.attachment;
  return ARCHIVE_CONSEQUENCE_COPY.member;
};

const getUnarchiveCopy = (itemType) => {
  if (itemType === 'vehicle') {
    return 'Unarchiving makes this vehicle available again. Removed dispatch boards are not restored.';
  }
  if (itemType === 'plow' || itemType === 'doubledown') {
    return 'Unarchiving makes this attachment available again. Removed dispatch boards and cleared saved-board attachments are not restored.';
  }
  if (itemType === 'staff') {
    return 'Unarchiving makes this staff member available again. Removed allocations, equipment assignments, dispatch boards, and saved-board associations are not restored.';
  }
  return 'Unarchiving makes this item available again. Removed dispatch boards and saved-board associations are not restored.';
};

const getResourcePath = (itemType) => {
  const path = RESOURCE_PATHS[itemType];
  if (!path) {
    throw new Error(`Unsupported archive item type: ${itemType}`);
  }
  return path;
};

const getPreviewFromError = (error) => error?.response?.data;

const formatCount = (count, singular, plural = `${singular}s`) => {
  const safeCount = Number(count || 0);
  return `${safeCount} ${safeCount === 1 ? singular : plural}`;
};

export const formatArchivedBy = (value) => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  return value.email || value.username || value.name || value.id || '—';
};

const ArchivePreviewContent = ({ preview, itemType }) => {
  const setups = preview?.setups || [];
  const lockedSetups = preview?.locked_setups || [];
  const live = preview?.live || [];
  const warningMessages = [
    ...(setups.some((setup) => setup.is_active)
      ? ['This item is in the currently active plan. Archiving removes it from live operations.']
      : []),
    ...(itemType === 'site'
      ? ['Archiving a site removes it from editable and live loader, walkway, salting, and equipment assignments. Allocations in locked setup history, historical jobs, and dispatch logs are preserved.']
      : []),
    ...(live.length > 0
      ? ['Live allocation rows were detected. These will be removed or reconciled during archive so live operations stay aligned.']
      : []),
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Paragraph style={{ marginBottom: 0 }}>
        Archiving this {TYPE_LABELS[itemType] || 'item'} will remove it from editable planning while
        preserving allocations in locked historical setups. The item will remain in the Archived tab.
      </Paragraph>
      <Alert type="warning" showIcon message={getArchiveConsequenceCopy(itemType)} />

      {preview?.archived && (
        <Alert type="info" showIcon message="This item is already archived." />
      )}

      {warningMessages.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
              {warningMessages.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}

      <div>
        <Text strong>Usage summary: </Text>
        <Text>
          {formatCount(preview?.summary?.setup_count, 'setup')} and{' '}
          {formatCount(preview?.summary?.allocation_count, 'allocation')}
        </Text>
      </div>

      <AssignmentList setups={setups} emptyText="No allocation setup usage found." renderSetupExtra={(setup) =>
        (setup.warnings || []).length > 0 && <Alert type="warning" showIcon
          message="Finalized allocation setup"
          description="This item is allocated in a finalized section of this setup. Archiving will still deallocate it from this finalized setup and cannot be automatically restored." />
      } />

      {lockedSetups.length > 0 && (
        <div>
          <Text strong>Preserved in locked history:</Text>
          <List
            size="small"
            bordered
            dataSource={lockedSetups}
            renderItem={(setup) => (
              <List.Item>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text strong>{setup.setup_name || `Setup ${setup.setup_id}`}</Text>
                    <Tag color="purple">Locked</Tag>
                  </Space>
                  <Space wrap>
                    {(setup.roles || []).map((role) => (
                      <Tag key={`${setup.setup_id}-${role}`} color="purple">{role}</Tag>
                    ))}
                  </Space>
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}

      {live.length > 0 && (
        <div>
          <Text strong>Live rows:</Text>
          <List
            size="small"
            dataSource={live}
            renderItem={(role) => <List.Item>{role}</List.Item>}
          />
        </div>
      )}
    </Space>
  );
};

export const ArchiveWithAllocationsButton = ({
  itemType,
  itemId,
  itemLabel,
  onArchived,
  disabled = false,
  buttonProps = {},
}) => {
  const [preview, setPreview] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resourcePath = getResourcePath(itemType);
  const label = itemLabel || `${TYPE_LABELS[itemType] || 'item'} ${itemId}`;

  const archive = async ({ previewOverride = preview } = {}) => {
    setSubmitting(true);
    try {
      const response = await api.post(`${resourcePath}/${itemId}/archive/`, {
        confirmed: true,
        force: false,
        expected_preview_hash: previewOverride?.preview_hash,
      });
      const summary = response.data?.summary || {};
      message.success(
        `Archived successfully. Removed from ${formatCount(summary.setup_count, 'setup')} and ${formatCount(summary.allocation_count, 'allocation')}; preserved ${formatCount(summary.locked_allocation_count, 'locked allocation')}.`
      );
      setModalVisible(false);
      setPreview(null);
      onArchived?.(response.data);
    } catch (error) {
      if (error?.response?.status === 409 && getPreviewFromError(error)) {
        setPreview(getPreviewFromError(error));
        setModalVisible(true);
        message.warning('Archive usage changed. Review the updated allocation list before confirming.');
      } else {
        message.error(error?.response?.data?.detail || error?.response?.data?.error || 'Failed to archive item.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmArchive = async () => {
    await archive();
  };

  const openArchiveFlow = async () => {
    setLoadingPreview(true);
    try {
      const response = await api.get(`${resourcePath}/${itemId}/archive-preview/`);
      const nextPreview = response.data;
      setPreview(nextPreview);

      if (!nextPreview?.in_use && (nextPreview?.live || []).length === 0) {
        Modal.confirm({
          title: `Archive ${label}?`,
          content: `This will move the ${TYPE_LABELS[itemType] || 'item'} to the Archived tab. ${getArchiveConsequenceCopy(itemType)}`,
          okText: 'Archive',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => archive({ previewOverride: nextPreview }),
        });
      } else {
        setModalVisible(true);
      }
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Failed to load archive preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <>
      <Button
        danger
        icon={<DeleteOutlined />}
        loading={loadingPreview}
        disabled={disabled}
        onClick={openArchiveFlow}
        {...buttonProps}
      >
        {buttonProps.children || 'Archive'}
      </Button>

      <Modal
        title={`Archive ${label}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={760}
        okText="Deallocate everywhere & archive"
        okButtonProps={{ danger: true, loading: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        onOk={confirmArchive}
      >
        <ArchivePreviewContent preview={preview} itemType={itemType} />
      </Modal>
    </>
  );
};

export const UnarchiveButton = ({
  itemType,
  itemId,
  itemLabel,
  onUnarchived,
  disabled = false,
  buttonProps = {},
}) => {
  const [submitting, setSubmitting] = useState(false);
  const resourcePath = getResourcePath(itemType);
  const label = itemLabel || `${TYPE_LABELS[itemType] || 'item'} ${itemId}`;

  const unarchive = () => {
    Modal.confirm({
      title: `Unarchive ${label}?`,
      content: getUnarchiveCopy(itemType),
      okText: 'Unarchive',
      cancelText: 'Cancel',
      onOk: async () => {
        setSubmitting(true);
        try {
          const response = await api.post(`${resourcePath}/${itemId}/unarchive/`);
          message.success('Unarchived successfully.');
          onUnarchived?.(response.data);
        } catch (error) {
          message.error(error?.response?.data?.detail || 'Failed to unarchive item.');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  return (
    <Button
      icon={<UndoOutlined />}
      loading={submitting}
      disabled={disabled}
      onClick={unarchive}
      {...buttonProps}
    >
      {buttonProps.children || 'Unarchive'}
    </Button>
  );
};
